import { Injectable } from "@angular/core";
import { DwQueryResult, sortArrayByField } from "@devwareapps/devware-cap";
import { RoSchoolCourseEnrollmentStatsEntity } from "../../../meta-data/app-meta-data.service";
import { RiskLevel, StudentAtRiskInfo } from "../models/ui-models/student-at-risk-info.model";
import { StudentAtRiskDetails } from "../models/ui-models/student-at-risk-details.model";
import { SchoolCourseStatEntity } from "../models/stat-entities/school-course-stat-entity.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { InstructorAssignmentStatEntity, InstructorStatEntity } from "../models/stat-entities/instructor-stat-entity.model";
import { ModuleOverviewEntity } from "../models/stat-entities/module-overview-entity.model";
import { SchoolReportingFilters } from "../models/school-reporting-filters.model";
import { DrillDownDetails, DrillDownType, ReferenceInfo } from "../models/ui-models/drill-down-details.model";

@Injectable({
    providedIn: 'root'
})
export class SchoolStatUtilService {


    constructor(private dateTimeUtilService: DateTimeUtilService) {

    }

    buildDrillDownFilters(drillDownType: DrillDownType, refernceInfo: ReferenceInfo, referenceData: RoSchoolCourseEnrollmentStatsEntity[]): DrillDownDetails {
        const drillDownDetails: DrillDownDetails = {
            drillDownType: drillDownType,
            referenceInfo: refernceInfo,
            drillDownTitle: this.buildDrillDownDescription(drillDownType, refernceInfo),
            filters: {} as SchoolReportingFilters,
            studentCourseIds: []
        };

        drillDownDetails.studentCourseIds = referenceData.filter(item => this.isDrillDownMatch(item, drillDownType, refernceInfo)).map(item => item.StudentCourseId);

        if (drillDownDetails.studentCourseIds.length === 0) {
            drillDownDetails.studentCourseIds = [-1]; // No matches, so set to an impossible value
        }

        return drillDownDetails;
    }

    buildDrillDownDescription(drillDownType: DrillDownType, refernceInfo: ReferenceInfo): string {
     switch(drillDownType) {
            case DrillDownType.studentsEnrolledInCourse:
                return `Enrolled Students in Course ${refernceInfo.referenceDescription}`;
            case DrillDownType.studentsEnrolled:
                return 'Enrolled Students';
            case DrillDownType.assignmentsPastDue:
                return 'Students with Past Due Assignments';
            case DrillDownType.acsTestTaken:
                return 'Students with ACS Tests Taken';
            case DrillDownType.acsTestAwaitingUpload:
                return 'Students Awaiting ACS Test Upload';
        }
    }

    isDrillDownMatch(item: RoSchoolCourseEnrollmentStatsEntity, drillDownType: DrillDownType, refernceInfo: ReferenceInfo): unknown {
        switch(drillDownType) {
            case DrillDownType.studentsEnrolledInCourse:
                return item.CourseId === refernceInfo.referenceKey && item.ActiveStudent;
            case DrillDownType.studentsEnrolled:
                return item.ActiveStudent;
            case DrillDownType.assignmentsPastDue:
                return item.PastDueAssignments > 0;
            case DrillDownType.acsTestTaken:
                return item.AcsTestUploaded > 0;
            case DrillDownType.acsTestAwaitingUpload:
                return item.AcsAwaitingUpload > 0;
        }

        return false;
    }

    buildAtRiskStudentList(atRiskStudents: RoSchoolCourseEnrollmentStatsEntity[], courseStats: SchoolCourseStatEntity[]): StudentAtRiskDetails {
        const result: StudentAtRiskDetails = {
            studentsAtRisk: atRiskStudents.map(item => this.mapToStudentAtRiskInfo(item)).filter(s => s.RiskNumber > 0),
        };

        result.studentsAtRisk = sortArrayByField(result.studentsAtRisk, 'RiskNumber', null, true);

        result.highRiskStudentCount = result.studentsAtRisk.filter(s => s.Risk === RiskLevel.high && !s.Hidden).length;
        result.mediumRiskStudentCount = result.studentsAtRisk.filter(s => s.Risk === RiskLevel.medium  && !s.Hidden).length;
        result.lowRiskStudentCount = result.studentsAtRisk.filter(s => s.Risk === RiskLevel.low  && !s.Hidden).length;
        result.hiddenStudentCount = result.studentsAtRisk.filter(s => s.Hidden).length;
        result.allRiskStudentCount = result.studentsAtRisk.filter(s => !s.Hidden).length;
        result.totalRiskStudentCount = result.studentsAtRisk.length;
        
        return result;
    }


    mapToStudentAtRiskInfo(item: RoSchoolCourseEnrollmentStatsEntity): StudentAtRiskInfo {
        const studentAtRiskInfo: StudentAtRiskInfo = {
            ...item,
            Tags: [],
            RiskNumber: 0,
            Hidden : item.HideInReporting
        };

        this.determineRiskLevel(studentAtRiskInfo);

        studentAtRiskInfo.RiskClass = studentAtRiskInfo.Risk ? studentAtRiskInfo.Risk.toString().toLowerCase() : '';
        

        return studentAtRiskInfo as StudentAtRiskInfo;
    }

    determineRiskLevel(item: StudentAtRiskInfo): void {
        let riskNumber = 0;

        if (item.PastDueAssignments > 0) {
            item.Tags.push('Past Due');

            riskNumber += item.PastDueAssignments; // Might need a weight here
        }

        if (!item.LastActivityDateTime) {
            item.Tags.push('Not Started');

            riskNumber += 5; // Might need a weight here
        } else {
            const lastActivityDateTime = item.LastActivityDateTime || item.CreatedDateTime;

            const daysWithoutActivity = this.dateTimeUtilService.calcDurationMinutes(lastActivityDateTime, this.dateTimeUtilService.getCurrentDateTime()) / (60 * 24);

            if (daysWithoutActivity > 7) {
                item.Tags.push('No Activity');

                let riskAdjustment = Math.floor(daysWithoutActivity / 7);

                if (riskAdjustment > 5) {
                    riskAdjustment = 5;
                }

                riskNumber += riskAdjustment; // Might need a weight here
            }
        }

        if (riskNumber >= 5) {
            item.Risk = RiskLevel.high;
        } else if (riskNumber >= 2) {
            item.Risk = RiskLevel.medium;
        } else if (riskNumber >= 1) {
            item.Risk = RiskLevel.low;
        }

        item.RiskNumber = riskNumber;
    }


    buildInstructorRankings(instructorStats: InstructorStatEntity[]): InstructorStatEntity[] {
        let results = [...instructorStats];

        // Populate AverageAssignmentsPerWeek from the prefetch data
        //   This is returned as a many to one since it could return one row per week, however, we made it an aggregate query so there should only be one row per instructor
        results.forEach(instructorStat => {

            this.processInstructorStat(instructorStat);

        });

        results = sortArrayByField(results, 'Weight', null, true);

        // Set the rank
        results.forEach((instructorStat, index) => {
            instructorStat.Rank = index + 1;
        });

        return results;
    }

    processInstructorStat(instructorStat: InstructorStatEntity) {
        const assignmentStats: InstructorAssignmentStatEntity = instructorStat.RoInstructorAssignmentsPerWeek[0] as InstructorAssignmentStatEntity;

        instructorStat.AverageAssignmentsPerWeek = assignmentStats?.AverageAssignmentsPerWeek || 0;

        instructorStat.CompletionRate = instructorStat.ActiveStudents > 0 ? (instructorStat.CompletedStudents / instructorStat.ActiveStudents) * 100 : 0;
        instructorStat.ACSAverageFirstTimePassRate = (instructorStat.ACSAverageFirstTimePassRate * 100) || 0;

        instructorStat.ACSTestsTakenText = `${instructorStat.ACSTestsUploaded || 0} Test${(instructorStat.ACSTestsUploaded || 0) !== 1 ? 's' : ''}`;
        //instructorStat.ACSTestsUploaded
        let weight = 0;

        weight += 0.3 * instructorStat.CompletionRate;
        weight += 0.5 * instructorStat.ACSAverageFirstTimePassRate;

        weight += 0.2 * ((instructorStat.ActiveStudents > 100, 100, instructorStat.ActiveStudents) / 100);

        instructorStat.Weight = weight;
    }

    buildModuleOverview(moduleOverviews: ModuleOverviewEntity[]): ModuleOverviewEntity[] {
        let results = [...moduleOverviews];

        // Populate AverageAssignmentsPerWeek from the prefetch data
        //   This is returned as a many to one since it could return one row per week, however, we made it an aggregate query so there should only be one row per instructor
        results.forEach(moduleOverview => {

            this.processModuleOverview(moduleOverview);

        });

        results = sortArrayByField(results, 'RetakeRatePercentage', null, true);

        return results;
    }

    processModuleOverview(moduleOverview: ModuleOverviewEntity) {
        moduleOverview.RetakeRatePercentage = 0;
        if (moduleOverview.TotalTakeCount > 0) {
            moduleOverview.RetakeRatePercentage = (moduleOverview.TotalFailCount / moduleOverview.TotalTakeCount) * 100;
        }

        moduleOverview.AverageTimeFormatted = this.dateTimeUtilService.formatDurationMinutesSeconds(moduleOverview.AverageTime || 0, { showSeconds: false, showText: true });
        moduleOverview.AverageTimePerSessionFormatted = this.dateTimeUtilService.formatDurationMinutesSeconds(moduleOverview.AverageTimePerSession || 0, { showSeconds: false, showText: true });
    }

}